import { useQuery } from '@tanstack/react-query';
import { useEffect, type JSX } from 'react';
import { Controller } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ScopeMultiSelect, type ScopeOption } from '@/components/users/ScopeMultiSelect';
import {
  useActiveSitesForFilterQuery,
  useActiveWorkOrdersForFilterQuery,
} from '@/hooks/useReferenceDataQueries';
import { useZodForm } from '@/lib/forms/useZodForm';
import { listRolesRequest } from '@/services/roles';
import type { PaginatedResult, RoleListItem } from '@/types/roles';

/**
 * Plan 08-02 D-82 UserForm — shared between create and edit modes.
 *
 * Schema rules:
 *  - fullName: 2..120 chars
 *  - email: zod's `.email()` (RFC 5322-ish)
 *  - employeeId: optional; if provided, 3..30 alnum + hyphens
 *  - phoneNumber: optional; if provided, 7..20 digits with optional leading `+`
 *  - roleId: required positive int (driven by an active-roles dropdown)
 *  - siteIds / workOrderIds: the user's scope grants. Multi-select, because a
 *    Maker / Checker / Approver can be assigned to several sites and several work
 *    orders at once; the two tiers combine by union, not precedence.
 *  - In CREATE mode only: optional password (≥12 chars w/ complexity) and a
 *    `sendResetLink` boolean (default false). In v1.0 the link path is wired
 *    to the backend's 501 — UI surfaces that on submit; we still let users tick
 *    the box so when v1.1 enables it, the form is ready.
 *  - In EDIT mode: password fields hidden; only profile + roleId.
 */

const PASSWORD_REGEX =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]).{12,}$/;

const userFormSchema = z
  .object({
    fullName: z.string().min(2, 'At least 2 characters').max(120, 'At most 120 characters'),
    email: z.string().email('Enter a valid email address').max(256, 'At most 256 characters'),
    employeeId: z
      .string()
      .regex(/^[A-Za-z0-9-]{3,30}$/, '3-30 letters, digits, hyphens')
      .optional()
      .or(z.literal('').transform(() => undefined)),
    phoneNumber: z
      .string()
      .regex(/^\+?[0-9]{7,20}$/, '7-20 digits, optional leading +')
      .optional()
      .or(z.literal('').transform(() => undefined)),
    roleId: z
      .number({ invalid_type_error: 'Pick a role' })
      .int('Pick a role')
      .positive('Pick a role'),
    // Scope grants. Sites and work orders are independent and combine by UNION:
    // holding sites {A,B} and WO-9 grants everything at A and B PLUS everything
    // belonging to WO-9, including WO-9's records at a site not held.
    //
    // Both empty is legitimate (it clears the assignment), but an unscoped
    // non-Super-Admin sees no work orders and cannot create a logsheet — the form
    // warns rather than blocks, since Super Admins and admin-only accounts have no
    // reason to be scoped at all.
    siteIds: z.array(z.number().int().positive()),
    workOrderIds: z.array(z.number().int().positive()),
    // Create-only fields. The schema accepts them in both modes; the UI hides
    // the inputs in edit mode so RHF never registers them.
    password: z
      .string()
      .regex(PASSWORD_REGEX, 'At least 12 chars including upper, lower, digit, and a symbol')
      .optional()
      .or(z.literal('').transform(() => undefined)),
    sendResetLink: z.boolean().optional(),
  })
  .strict();

export type UserFormValues = z.infer<typeof userFormSchema>;

export interface UserFormProps {
  readonly mode: 'create' | 'edit';
  readonly initialValues?: Partial<UserFormValues>;
  readonly onSubmit: (values: UserFormValues) => void | Promise<void>;
  readonly submitting?: boolean;
  readonly errorMessage?: string | null;
}

const EMPTY_DEFAULTS: Partial<UserFormValues> = {
  fullName: '',
  email: '',
  employeeId: '',
  phoneNumber: '',
  password: '',
  sendResetLink: false,
  siteIds: [],
  workOrderIds: [],
};

export function UserForm({
  mode,
  initialValues,
  onSubmit,
  submitting = false,
  errorMessage,
}: UserFormProps): JSX.Element {
  const defaults: Partial<UserFormValues> = {
    ...EMPTY_DEFAULTS,
    ...initialValues,
  };

  const form = useZodForm({
    schema: userFormSchema,
    defaultValues: defaults,
  });

  // Re-sync defaults when initialValues changes (UserEditPage hydrates async).
  useEffect(() => {
    if (initialValues !== undefined) {
      form.reset({ ...EMPTY_DEFAULTS, ...initialValues });
    }
    // form.reset is stable; intentionally only depend on initialValues identity
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  const rolesQuery = useQuery<PaginatedResult<RoleListItem>>({
    queryKey: ['roles', 'active', 100],
    queryFn: () => listRolesRequest({ status: 'active', page: 1, pageSize: 100 }),
  });

  const roleOptions = rolesQuery.data?.items ?? [];

  const sitesQuery = useActiveSitesForFilterQuery();
  const siteOptions: ScopeOption[] = (sitesQuery.data?.items ?? []).map((s) => ({
    id: s.id,
    label: s.name,
    sublabel: s.code,
  }));

  const workOrdersQuery = useActiveWorkOrdersForFilterQuery();
  const workOrderOptions: ScopeOption[] = (workOrdersQuery.data?.items ?? []).map((w) => ({
    id: w.id,
    label: w.woNumber,
    sublabel: w.clientName,
  }));

  const isUnscoped =
    form.watch('siteIds').length === 0 && form.watch('workOrderIds').length === 0;

  return (
    <form
      onSubmit={(e) => {
        void form.handleSubmit(onSubmit)(e);
      }}
      className="space-y-6"
      noValidate
      data-testid={`user-form-${mode}`}
    >
      {errorMessage != null && errorMessage !== '' && (
        <div
          role="alert"
          data-testid="user-form-error"
          className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive"
        >
          {errorMessage}
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="user-fullname">Full name</Label>
        <Input
          id="user-fullname"
          type="text"
          autoComplete="name"
          disabled={submitting}
          aria-invalid={form.formState.errors.fullName !== undefined}
          {...form.register('fullName')}
          data-testid="user-form-fullname"
        />
        {form.formState.errors.fullName && (
          <p className="text-xs text-destructive" data-testid="user-form-fullname-error">
            {form.formState.errors.fullName.message}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="user-email">Email</Label>
        <Input
          id="user-email"
          type="email"
          autoComplete="email"
          disabled={submitting}
          aria-invalid={form.formState.errors.email !== undefined}
          {...form.register('email')}
          data-testid="user-form-email"
        />
        {form.formState.errors.email && (
          <p className="text-xs text-destructive" data-testid="user-form-email-error">
            {form.formState.errors.email.message}
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="user-employeeid">Employee ID (optional)</Label>
          <Input
            id="user-employeeid"
            type="text"
            autoComplete="off"
            disabled={submitting}
            aria-invalid={form.formState.errors.employeeId !== undefined}
            {...form.register('employeeId')}
            data-testid="user-form-employeeid"
          />
          {form.formState.errors.employeeId && (
            <p className="text-xs text-destructive" data-testid="user-form-employeeid-error">
              {form.formState.errors.employeeId.message}
            </p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="user-phone">Phone (optional)</Label>
          <Input
            id="user-phone"
            type="tel"
            autoComplete="tel"
            disabled={submitting}
            aria-invalid={form.formState.errors.phoneNumber !== undefined}
            {...form.register('phoneNumber')}
            data-testid="user-form-phone"
          />
          {form.formState.errors.phoneNumber && (
            <p className="text-xs text-destructive" data-testid="user-form-phone-error">
              {form.formState.errors.phoneNumber.message}
            </p>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="user-role">Role</Label>
        <Controller
          control={form.control}
          name="roleId"
          render={({ field }) => (
            <Select
              value={typeof field.value === 'number' && field.value > 0 ? String(field.value) : ''}
              onValueChange={(v) => {
                const n = Number(v);
                if (Number.isFinite(n) && n > 0) field.onChange(n);
              }}
              disabled={submitting || rolesQuery.isLoading}
            >
              <SelectTrigger id="user-role" data-testid="user-form-role-trigger">
                <SelectValue placeholder="Select a role…" />
              </SelectTrigger>
              <SelectContent>
                {roleOptions.map((r) => (
                  <SelectItem
                    key={r.id}
                    value={String(r.id)}
                    data-testid={`user-form-role-option-${String(r.id)}`}
                  >
                    {r.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
        {form.formState.errors.roleId && (
          <p className="text-xs text-destructive" data-testid="user-form-role-error">
            {form.formState.errors.roleId.message}
          </p>
        )}
      </div>

      <div className="space-y-4 rounded-md border p-4">
        <div className="space-y-1">
          <p className="text-sm font-medium">Data scope</p>
          <p className="text-xs text-muted-foreground">
            Sites and work orders are separate grants that add together. A user assigned two
            sites and one work order sees everything at both sites, plus everything belonging to
            that work order — including its records at sites they were not given.
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="user-form-site-filter">Sites</Label>
          <Controller
            control={form.control}
            name="siteIds"
            render={({ field }) => (
              <ScopeMultiSelect
                idPrefix="user-form-site"
                options={siteOptions}
                value={Array.isArray(field.value) ? field.value : []}
                onChange={field.onChange}
                disabled={submitting}
                loading={sitesQuery.isLoading}
                filterPlaceholder="Filter sites…"
                emptyText="No active sites exist yet."
                noMatchText="No sites match that filter."
              />
            )}
          />
          {form.formState.errors.siteIds && (
            <p className="text-xs text-destructive" data-testid="user-form-site-error">
              {form.formState.errors.siteIds.message}
            </p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="user-form-workorder-filter">Work orders (optional)</Label>
          <Controller
            control={form.control}
            name="workOrderIds"
            render={({ field }) => (
              <ScopeMultiSelect
                idPrefix="user-form-workorder"
                options={workOrderOptions}
                value={Array.isArray(field.value) ? field.value : []}
                onChange={field.onChange}
                disabled={submitting}
                loading={workOrdersQuery.isLoading}
                filterPlaceholder="Filter work orders…"
                emptyText="No active work orders exist yet."
                noMatchText="No work orders match that filter."
              />
            )}
          />
          {form.formState.errors.workOrderIds && (
            <p className="text-xs text-destructive" data-testid="user-form-workorder-error">
              {form.formState.errors.workOrderIds.message}
            </p>
          )}
        </div>

        <p className="text-xs text-muted-foreground" data-testid="user-form-site-hint">
          {isUnscoped
            ? 'Without a site or work order, this user sees no work orders and cannot create logsheets. Leave unset only for Super Admins and admin-only accounts.'
            : 'Determines which work orders, machines and logsheets this user can see and create.'}
        </p>
      </div>

      {mode === 'create' && (
        <div className="space-y-4 rounded-md border bg-muted/20 p-4">
          <div className="space-y-1">
            <p className="text-sm font-medium">Initial password</p>
            <p className="text-xs text-muted-foreground">
              Leave blank to have the system generate a one-time temporary password. The user will
              be required to change it on first login.
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="user-password">Password (optional)</Label>
            <Input
              id="user-password"
              type="password"
              autoComplete="new-password"
              disabled={submitting}
              aria-invalid={form.formState.errors.password !== undefined}
              {...form.register('password')}
              data-testid="user-form-password"
            />
            {form.formState.errors.password && (
              <p className="text-xs text-destructive" data-testid="user-form-password-error">
                {form.formState.errors.password.message}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <input
              id="user-sendresetlink"
              type="checkbox"
              disabled={submitting}
              {...form.register('sendResetLink')}
              data-testid="user-form-sendresetlink"
              className="h-4 w-4 rounded border-input"
            />
            <Label htmlFor="user-sendresetlink">Email a reset link instead (v1.1+)</Label>
          </div>
        </div>
      )}

      <div className="flex items-center gap-2">
        <Button type="submit" disabled={submitting} data-testid="user-form-submit">
          {submitting
            ? mode === 'create'
              ? 'Creating…'
              : 'Saving…'
            : mode === 'create'
              ? 'Create user'
              : 'Save changes'}
        </Button>
        <Button asChild variant="outline" type="button">
          <Link to="/users" data-testid="user-form-cancel">
            Cancel
          </Link>
        </Button>
      </div>
    </form>
  );
}
